/**
 * @fileoverview Saved guides library ("Studio Quiet") — every teaching guide
 * the instructor has saved from the repertoire assistant, on one page.
 *
 * The assistant's slide-out panel is a quick reach for the last few guides;
 * this is where the whole library lives: searchable by piece, student or
 * type, openable in place, and deletable behind a confirmation with an undo.
 * Mirrors the mobile SavedRepertoiresScreen (dashboard "View all" and the
 * assistant both land here).
 *
 * Delete commits immediately and the toast's Undo re-saves the same guide
 * through POST /repertoire/save — the ClassDetail roster pattern — so a
 * navigation mid-toast can never leave a phantom row. The restored guide
 * carries a fresh id and save date, which is why it goes back to the top.
 */

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Bookmark, BookOpen, RotateCcw, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { AuthContext } from "@/App";
import api, { getErrorMessage } from "@/lib/api";
import { accountFeatureEnabled } from "@/lib/orgFeatures";
import { Button, Card, Eyebrow, ListRow } from "@/components/studio/primitives";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RepertoireGuide } from "@/components/RepertoireGuide";

const DATE_FMT = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" });

/** "Mar 4, 2026" for a saved guide's timestamp; empty when there is none. */
const savedOn = (item) => {
  if (!item?.created_at) return "";
  const d = new Date(item.created_at);
  return Number.isNaN(d.getTime()) ? "" : DATE_FMT.format(d);
};

/** Everything a search box should be able to find a guide by. */
const searchText = (item) =>
  [item.title, item.student_name, item.type, item.query].filter(Boolean).join(" ").toLowerCase();

/** The fields POST /repertoire/save takes — what an Undo sends back. */
const saveBody = (item) => ({
  title: item.title,
  type: item.type,
  query: item.query,
  sections: item.sections,
  student_id: item.student_id || null,
  student_name: item.student_name || null,
});

const SavedRepertoiresPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  // The org toggle 403s every /repertoire route. The assistant page already
  // explains that state; a second copy of the explanation here would drift.
  const repertoireDisabled = !accountFeatureEnabled(user, "repertoire");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState("");
  const [viewing, setViewing] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const abortRef = useRef(null);

  const load = useCallback(async ({ refresh = false } = {}) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    if (refresh) setRefreshing(true); else setLoading(true);
    setLoadError(null);
    try {
      const res = await api.get("/repertoire/saved", { signal: controller.signal });
      if (controller.signal.aborted) return;
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (controller.signal.aborted) return;
      setLoadError(getErrorMessage(err, "Couldn't load your saved guides"));
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    if (repertoireDisabled) return undefined;
    load();
    return () => abortRef.current?.abort();
  }, [load, repertoireDisabled]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => searchText(item).includes(q));
  }, [items, search]);

  const restore = async (item) => {
    try {
      const { data } = await api.post("/repertoire/save", saveBody(item));
      setItems((prev) => [data, ...prev.filter((s) => s.repertoire_id !== data.repertoire_id)]);
      toast.success("Guide restored");
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't restore the guide"));
    }
  };

  const confirmDelete = async () => {
    const item = pendingDelete;
    if (!item) return;
    setPendingDelete(null);
    const index = items.findIndex((s) => s.repertoire_id === item.repertoire_id);
    setItems((prev) => prev.filter((s) => s.repertoire_id !== item.repertoire_id));
    if (viewing?.repertoire_id === item.repertoire_id) setViewing(null);
    try {
      await api.delete(`/repertoire/saved/${item.repertoire_id}`);
      toast.success(`Deleted "${item.title}"`, {
        action: { label: "Undo", onClick: () => restore(item) },
      });
    } catch (err) {
      // The row comes back where it was; nothing was removed server-side.
      setItems((prev) => {
        const next = prev.filter((s) => s.repertoire_id !== item.repertoire_id);
        next.splice(Math.min(Math.max(index, 0), next.length), 0, item);
        return next;
      });
      toast.error(getErrorMessage(err, "Failed to delete"));
    }
  };

  if (repertoireDisabled) return <Navigate to="/repertoire" replace />;

  const count = items.length;
  const subtitle = loading
    ? "Loading your library…"
    : count === 0
      ? "Nothing saved yet"
      : `${count} saved ${count === 1 ? "guide" : "guides"}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mx-auto max-w-[1180px] px-6 pb-32 pt-11 md:px-12"
      data-testid="saved-repertoires-page"
    >
      {/* Header */}
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <Eyebrow icon="book">Repertoires</Eyebrow>
          <h1 className="mt-1.5 font-serif text-[44px] font-normal leading-none tracking-[-0.02em] text-[var(--text-primary)]">
            Saved guides
          </h1>
          <p className="mt-2 font-sans text-sm text-[var(--text-tertiary)]" data-testid="saved-repertoires-count">{subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => load({ refresh: true })}
            disabled={loading || refreshing}
            aria-label="Refresh saved guides"
            data-testid="saved-repertoires-refresh"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-[var(--studio-divider)] bg-[var(--studio-raised)] text-[var(--text-secondary)] transition-colors hover:bg-[var(--studio-interactive)] hover:text-[var(--text-primary)] disabled:opacity-50"
          >
            <RotateCcw className={`h-[18px] w-[18px] ${refreshing ? "animate-spin" : ""}`} strokeWidth={1.5} />
          </button>
          <Button variant="ghost" onPress={() => navigate("/repertoire")} data-testid="saved-repertoires-new">
            <BookOpen className="h-4 w-4" strokeWidth={1.5} />
            Ask Hans
          </Button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {viewing ? (
          <motion.div
            key={`guide-${viewing.repertoire_id}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className="mx-auto max-w-3xl"
            data-testid="saved-repertoire-view"
          >
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setViewing(null)}
                data-testid="saved-repertoire-back"
                className="flex items-center gap-2 rounded-lg px-3 py-2 font-sans text-sm text-[var(--text-tertiary)] transition-colors hover:bg-[var(--studio-interactive)] hover:text-[var(--text-primary)]"
              >
                <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                All saved guides
              </button>
              <button
                type="button"
                onClick={() => setPendingDelete(viewing)}
                data-testid="saved-repertoire-view-delete"
                className="flex items-center gap-2 rounded-lg px-3 py-2 font-sans text-sm text-[var(--text-tertiary)] transition-colors hover:bg-[var(--state-danger-dim)] hover:text-[var(--state-danger)]"
              >
                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                Delete
              </button>
            </div>
            <div className="mb-3 flex items-center gap-2 font-sans text-xs text-[var(--text-tertiary)]">
              <Bookmark className="h-3.5 w-3.5" strokeWidth={1.5} />
              Saved guide{savedOn(viewing) && ` — ${savedOn(viewing)}`}
              {viewing.student_name && ` — for ${viewing.student_name}`}
            </div>
            <RepertoireGuide result={viewing} />
          </motion.div>
        ) : (
          <motion.div
            key="library"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Search */}
            <div className="mb-5 flex items-center gap-2.5 rounded-[14px] border border-[var(--studio-divider)] bg-[var(--studio-raised)] px-4">
              <Search className="h-[17px] w-[17px] shrink-0 text-[var(--text-tertiary)]" strokeWidth={1.5} />
              <Input
                placeholder="Search by piece, student or type…"
                aria-label="Search saved guides"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-12 flex-1 border-none bg-transparent px-0 text-base text-[var(--text-primary)] shadow-none focus-visible:ring-0 placeholder:text-[var(--text-quaternary)]"
                data-testid="saved-repertoires-search"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                  className="flex h-7 w-7 items-center justify-center rounded-full text-[var(--text-tertiary)] transition-colors hover:bg-[var(--studio-interactive)] hover:text-[var(--text-primary)]"
                >
                  <X className="h-4 w-4" strokeWidth={1.5} />
                </button>
              )}
            </div>

            {loading ? (
              <SkeletonList />
            ) : loadError ? (
              <Card className="p-10 text-center" data-testid="saved-repertoires-error">
                <p className="mb-5 font-sans text-sm text-[var(--text-tertiary)]">{loadError}</p>
                <Button variant="ghost" onPress={() => load()} data-testid="saved-repertoires-retry">
                  <RotateCcw className="h-4 w-4" strokeWidth={1.5} />
                  Try again
                </Button>
              </Card>
            ) : count === 0 ? (
              <EmptyPanel
                testId="saved-repertoires-empty"
                title="No saved guides yet"
                sub="Ask Hans about a piece or a technique, then save the teaching guide to find it here."
                cta="Ask Hans"
                onCta={() => navigate("/repertoire")}
              />
            ) : filtered.length === 0 ? (
              <EmptyPanel
                testId="saved-repertoires-no-results"
                title={`No guides match “${search.trim()}”`}
                sub="Try the piece, the student's name or the guide type."
                cta="Clear search"
                onCta={() => setSearch("")}
              />
            ) : (
              <Card padding={6} data-testid="saved-repertoires-list">
                {filtered.map((item, i, arr) => (
                  <ListRow
                    key={item.repertoire_id}
                    data-testid={`saved-repertoire-${item.repertoire_id}`}
                    onPress={() => setViewing(item)}
                    borderBottom={i < arr.length - 1}
                    className="rounded-[14px]"
                    leading={
                      <div className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-[11px] bg-[var(--accent-yellow-dim)]">
                        <BookOpen className="h-[18px] w-[18px] text-[var(--accent-yellow-text)]" strokeWidth={1.5} />
                      </div>
                    }
                    title={item.title || "Untitled guide"}
                    subtitle={[item.student_name, item.type].filter(Boolean).join(" · ") || "General"}
                    meta={savedOn(item)}
                    trailing={
                      <button
                        type="button"
                        aria-label={`Delete ${item.title || "guide"}`}
                        data-testid={`saved-repertoire-delete-${item.repertoire_id}`}
                        onClick={(e) => { e.stopPropagation(); setPendingDelete(item); }}
                        onKeyDown={(e) => e.stopPropagation()}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[var(--text-quaternary)] opacity-0 transition-[opacity,color,background-color] hover:bg-[var(--state-danger-dim)] hover:text-[var(--state-danger)] focus:opacity-100 focus-visible:opacity-100 group-hover:opacity-100"
                      >
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                      </button>
                    }
                  />
                ))}
              </Card>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => { if (!open) setPendingDelete(null); }}>
        <AlertDialogContent className="mx-4 max-w-[calc(100%-2rem)] border-[var(--studio-divider-strong)] bg-[var(--studio-overlay)] text-[var(--text-primary)] sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this guide?</AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--text-secondary)]">
              “{pendingDelete?.title}” will be removed from your saved guides. You can undo this for a moment afterwards.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="touch-target border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)] text-[var(--text-primary)]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="saved-repertoire-delete-confirm"
              className="touch-target bg-[var(--state-danger)] text-[var(--text-on-state)]"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
};

const SkeletonList = () => (
  <div className="flex flex-col gap-2" data-testid="saved-repertoires-skeleton">
    {[1, 2, 3, 4].map((i) => (
      <div key={i} className="skeleton h-16 rounded-xl" />
    ))}
  </div>
);

const EmptyPanel = ({ testId, title, sub, cta, onCta }) => (
  <div className="rounded-[20px] border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-14 text-center" data-testid={testId}>
    <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[var(--studio-interactive)]">
      <Bookmark className="h-[26px] w-[26px] text-[var(--text-tertiary)]" strokeWidth={1.5} />
    </div>
    <h3 className="mb-2 font-serif text-xl font-normal text-[var(--text-primary)]">{title}</h3>
    <p className="mb-6 font-sans text-sm text-[var(--text-tertiary)]">{sub}</p>
    {cta && <Button variant="ghost" onPress={onCta}>{cta}</Button>}
  </div>
);

export default SavedRepertoiresPage;
